/** Adapter between the API lead (raw Prisma) and the UI `Lead` (spec §1). */

import type {
  ClientLevel,
  Lead,
  LeadContactEntry,
  LeadContactType,
  LeadSource,
  LeadStage,
  LeadStageEntryView,
} from "@/types";

export interface ApiLeadContact {
  id: string;
  type: LeadContactType;
  date: string;
  summary: string;
  followUpAt?: string | null;
}

export interface ApiLead {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  companyName?: string | null;
  sector?: string | null;
  appointmentAt?: string | null;
  projectLevel?: ClientLevel | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  source: LeadSource;
  serviceId?: string | null;
  estimatedValue?: number | string | null;
  stage: LeadStage;
  notes?: string | null;
  clientId?: string | null;
  service?: { id: string; name: string } | null;
  leadServices?: Array<{ service?: { id: string; name: string } | null }> | null;
  contacts?: ApiLeadContact[];
}

export interface ApiStageEntry {
  id: string;
  createdAt: string;
  fromStage: LeadStage;
  toStage: LeadStage;
  description?: string | null;
  clientInfo?: string | null;
  projectLevel?: ClientLevel | null;
  links: string[];
  quotePrice?: number | null;
  paymentTerms?: string | null;
  deliveryTime?: string | null;
  downPayment?: number | null;
  totalAmount?: number | null;
  files: Array<{ id: string; name: string; mimeType?: string | null; sizeBytes?: number | null }>;
}

/** Spec §1.1 — the new-lead form payload. */
export interface LeadInput {
  name: string;
  phone: string;
  address?: string;
  email: string;
  companyName: string;
  sector: string;
  /** Spec consolidée §1 — « Services intéressés » (multi, ≥ 1). */
  serviceIds: string[];
  appointmentAt: string;
  source: LeadSource;
  notes?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  tiktokUrl?: string;
}

/** Spec §1.3 — one transition pop-up payload. */
export interface TransitionInput {
  toStage: LeadStage;
  files: Array<{ name: string; mimeType?: string; dataBase64: string }>;
  links: string[];
  description?: string;
  clientInfo?: string;
  projectLevel?: ClientLevel;
  quotePrice?: number;
  paymentTerms?: string;
  deliveryTime?: string;
  tasks: Array<{
    title: string;
    dueDate: string;
    teamMemberId: string;
    checklist: string[];
    priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
    description?: string;
    projectStage?: string;
  }>;
  downPayment?: number;
  totalAmount?: number;
  /** ADR-001 — compte de trésorerie qui reçoit l'acompte (requis si acompte > 0). */
  downPaymentAccountId?: string;
}

function toNumberOrUndefined(value: number | string | null | undefined): number | undefined {
  if (value === null || value === undefined) return undefined;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : undefined;
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export function toLead(api: ApiLead): Lead {
  const contacts: LeadContactEntry[] = (api.contacts ?? []).map((c) => ({
    id: c.id,
    type: c.type,
    date: formatDate(c.date),
    summary: c.summary,
    followUpAt: c.followUpAt ? formatDate(c.followUpAt) : undefined,
  }));
  return {
    id: api.id,
    name: api.name,
    phone: api.phone ?? undefined,
    email: api.email ?? undefined,
    address: api.address ?? undefined,
    companyName: api.companyName ?? undefined,
    sector: api.sector ?? undefined,
    appointmentAt: api.appointmentAt ?? undefined,
    projectLevel: api.projectLevel ?? undefined,
    facebookUrl: api.facebookUrl ?? undefined,
    instagramUrl: api.instagramUrl ?? undefined,
    tiktokUrl: api.tiktokUrl ?? undefined,
    source: api.source,
    serviceId: api.serviceId ?? undefined,
    serviceName: api.service?.name,
    services: (api.leadServices ?? [])
      .map((ls) => ls.service)
      .filter((s): s is { id: string; name: string } => Boolean(s?.id && s?.name)),
    estimatedValue: toNumberOrUndefined(api.estimatedValue),
    stage: api.stage,
    notes: api.notes ?? undefined,
    clientId: api.clientId ?? undefined,
    contacts,
  };
}

export function toStageEntry(api: ApiStageEntry): LeadStageEntryView {
  return {
    id: api.id,
    createdAt: api.createdAt,
    fromStage: api.fromStage,
    toStage: api.toStage,
    description: api.description ?? undefined,
    clientInfo: api.clientInfo ?? undefined,
    projectLevel: api.projectLevel ?? undefined,
    links: api.links ?? [],
    quotePrice: toNumberOrUndefined(api.quotePrice),
    paymentTerms: api.paymentTerms ?? undefined,
    deliveryTime: api.deliveryTime ?? undefined,
    downPayment: toNumberOrUndefined(api.downPayment),
    totalAmount: toNumberOrUndefined(api.totalAmount),
    files: api.files.map((f) => ({
      id: f.id,
      name: f.name,
      mimeType: f.mimeType ?? undefined,
      sizeBytes: f.sizeBytes ?? undefined,
    })),
  };
}

export function toLeadWriteBody(input: LeadInput): Record<string, unknown> {
  return {
    name: input.name,
    phone: input.phone,
    email: input.email,
    companyName: input.companyName,
    sector: input.sector,
    serviceIds: input.serviceIds,
    appointmentAt: input.appointmentAt,
    source: input.source,
    ...(input.address ? { address: input.address } : {}),
    ...(input.notes ? { notes: input.notes } : {}),
    ...(input.facebookUrl ? { facebookUrl: input.facebookUrl } : {}),
    ...(input.instagramUrl ? { instagramUrl: input.instagramUrl } : {}),
    ...(input.tiktokUrl ? { tiktokUrl: input.tiktokUrl } : {}),
  };
}

/** FR labels for the 8 pipeline stages — shared by the CRM page and the client dossier timeline. */
export const LEAD_STAGE_LABEL: Record<LeadStage, string> = {
  NEW: "Nouveau",
  CONTACTED: "Contacté",
  IN_PROGRESS: "En cours de traitement",
  QUALIFIED: "Qualifié",
  NOT_QUALIFIED: "Non qualifié",
  QUOTE_PROPOSAL: "Devis / Proposition",
  ACTIVE: "Actif",
  INACTIVE: "Inactif",
};

/** Appointment proximity for the alert colors (spec §1.2). */
export type AppointmentUrgency = "past" | "red" | "orange" | "none";
export function appointmentUrgency(
  appointmentAt: string | undefined,
  now = Date.now(),
): AppointmentUrgency {
  if (!appointmentAt) return "none";
  const at = new Date(appointmentAt).getTime();
  if (Number.isNaN(at)) return "none";
  const diff = at - now;
  if (diff < 0) return "past";
  if (diff <= 60 * 60 * 1000) return "red";
  if (diff <= 24 * 60 * 60 * 1000) return "orange";
  return "none";
}
